import { dateUSFormat } from '../../../../../utils/dateTimeUtils'
import { WHITE } from '../../../../../scss/colorConsts'
import { VISIT_STATUS } from '../../../../../utils/statusConstants'

/** This returns what status category a particular status string is in, there is probably a better way to do this, but this will do for now.
 * See package.json (./src/package.json) for a list of current statuses. These 2 files should probably be kept in sync.
 * @param statusString A value from the API that is stored in the database.
 * @return string A string that is either 'red,' 'green,' 'orange,' or 'empty' depending on the status. It is used to both sort different lists and applying classes in the DOM for CSS to style.
 */
const getStatusCategory = (statusString) => {
  switch (statusString) {
    case VISIT_STATUS.available:
    case 'Cancelled':
    case VISIT_STATUS.last:
    case VISIT_STATUS.not_seen:
    case VISIT_STATUS.scheduled:
      return 'empty'
    case 'Bill audit':
    case 'Bill hold':
    case VISIT_STATUS.bill_submitted:
    case 'Claim accepted':
    case VISIT_STATUS.claim_assigned:
    case 'Claim denied':
    case 'Claim hold':
    case 'Claim processed':
    case 'Claim received':
    case 'Claim rejected':
    case VISIT_STATUS.completed:
    case 'Patient assigned':
    case 'Patient hold':
    case 'Patient payment':
    case 'Patient statement sent':
    case 'Settled':
      return 'green'
    case VISIT_STATUS.admit:
    case VISIT_STATUS.bill_pending:
    case VISIT_STATUS.checked_in:
    case 'Collections assigned':
    case 'Collections review':
      return 'red'
    case VISIT_STATUS.bill_rejected:
    case 'Claim provider hold': // Status is unused ?
    case VISIT_STATUS.super_bill: // Status is unused ?
      return 'warning'
    case 'Void':
      return WHITE
    default:
      return 'empty'
  }
}

/** Helper method that will capitalize the first letter in each word of a string e.g. Not yet submitted => Not Yet Submitted
 * @param words a string that is probably in any format (all lower case or all upper case)
 * @return string a formatted string with the first letter of each word capitalized and the rest of the word lowercase
 */
const capitalizeFirstLetterInSentence = (words = '') => {
  if (!words) {
    return ''
  }

  const arr = words.toLowerCase().trim().split(' ')
  for (let i = 0; i < arr.length; i++) {
    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1)
  }
  return arr.join(' ')
}

/** Small helper method that formulates a string that will be used as part of a CSS class.
 * @param title A string that is used as the label for different categories in the filter e.g. Encounters Status.
 * @return string A string created from the label in a format for being part of a CSS class e.g. "Encounter Status" => "encounter-status."
 */
const createClassStringFromTitle = (title) => {
  if (title.length) {
    return title.split(' ').join('-').toLowerCase()
  } else {
    return ''
  }
}

/**
 * Why must everything be hacky?  Takes in a rendering_provider_name and
 * tries to make it output First (Middle?) Last.
 * Some names can have a comma in it, some can have a DR. or M.D. appendage
 * So we want to remove them
 * @param name the string name
 * @return string name hopefully in first to last order, with no titles
 */
const reorderNameFirstToLastNoTitle = (name) => {
  if (!name) {
    return ''
  }
  let finalName = name.replace(/\bdr\.|\bm\.d\./gi, '')

  if (finalName.includes(',')) {
    const names = finalName.split(',')
    const cleanedParts = names.map((name) => name.trim())
    const [lastName, ...rest] = cleanedParts
    return [...rest, lastName].join(' ')
  } else {
    return finalName.trim()
  }
}

const getNameInitials = (name) => {
  const names = reorderNameFirstToLastNoTitle(name)?.split(' ')
  return names && names.length > 1 ? names[0].charAt(0) + names[names.length - 1].charAt(0) : ''
}

const getChargeProvidersDetails = (charge, incidentProviderList = [], canUseLastVisit = true) => {
  let date = ''
  let renderingName = ''
  let incidentName = ''

  if (
    charge.status === VISIT_STATUS.last &&
    charge.service_date_from &&
    charge.service_date_from != 'Invalid date'
  ) {
    // if its Last, we can use the item to generate an output
    renderingName = reorderNameFirstToLastNoTitle(charge.rendering_provider_name)
    date = dateUSFormat(charge.service_date_from)

    if (charge?.incident_to_provider) {
      incidentName =
        charge?.incident_to_provider?.first_name + ' ' + charge?.incident_to_provider?.last_name
    }
  } else if (
    canUseLastVisit &&
    charge.status !== VISIT_STATUS.last &&
    charge.lastVisit &&
    charge.lastVisit.service_date_from &&
    charge.lastVisit.service_date_from != 'Invalid date'
  ) {
    // else use the attached lastVisit object to make our initials
    renderingName = reorderNameFirstToLastNoTitle(charge.lastVisit.rendering_provider_name)

    if (charge?.lastVisit?.incident_to_provider_id) {
      const incident_to_provider = incidentProviderList.find(
        (p) => p?._id === charge?.lastVisit?.incident_to_provider_id,
      )
      if (incident_to_provider) {
        incidentName = incident_to_provider?.first_name + ' ' + incident_to_provider?.last_name
      }
    }

    date = dateUSFormat(charge.lastVisit.service_date_from)
  } else {
    // assume its a basic charge, and its data is trustworthy
    date = dateUSFormat(charge.service_date_from)
    renderingName = reorderNameFirstToLastNoTitle(charge?.rendering_provider_name)

    if (charge?.incident_to_provider_id) {
      const incident_to_provider = incidentProviderList.find(
        (p) => p?._id === charge?.incident_to_provider_id,
      )
      if (incident_to_provider) {
        incidentName = incident_to_provider?.first_name + ' ' + incident_to_provider?.last_name
      }
    }
  }

  return {
    date,
    renderingName,
    renderingInitials: getNameInitials(renderingName),
    incidentName,
    incidentInitials: getNameInitials(incidentName),
  }
}

export {
  getStatusCategory,
  createClassStringFromTitle,
  capitalizeFirstLetterInSentence,
  reorderNameFirstToLastNoTitle,
  getNameInitials,
  getChargeProvidersDetails,
}
